import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { CheckCircle, PartyPopper, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sendReservationConfirmation } from "@/functions/sendReservationConfirmation";

export default function PaymentSuccess() {
  const params = new URLSearchParams(window.location.search);
  const reservationId = params.get("reservation_id");

  const [reservation, setReservation] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reservationId) { setLoading(false); return; }

    base44.entities.Reservation.get(reservationId)
      .then(async (res) => {
        setReservation(res);
        // Mark as confirmed and paid
        await base44.entities.Reservation.update(reservationId, {
          reservation_status: "Confirmed",
          payment_status: "Reservation Fee Paid",
        });
        // Fetch product
        if (res.product_id) {
          const prod = await base44.entities.Product.get(res.product_id).catch(() => null);
          setProduct(prod);
          // Send confirmation email (extract email from notes field)
          const emailMatch = res.notes?.match(/Email: ([^\s,]+)/);
          const nameMatch = res.notes?.match(/Customer: ([^,]+)/);
          const phoneMatch = res.notes?.match(/Phone: ([^,]+)/);
          const cityMatch = res.notes?.match(/City: ([^,]+)/);
          const stateMatch = res.notes?.match(/State: ([^,]+)/);
          const zipMatch = res.notes?.match(/Zip: ([^,]+)/);
          if (emailMatch) {
            sendReservationConfirmation({
              reservationId: res.id,
              customerEmail: emailMatch[1],
              customerName: nameMatch?.[1]?.trim() || "",
              customerPhone: phoneMatch?.[1]?.trim() || "",
              deliveryAddress: res.delivery_address,
              customerCity: cityMatch?.[1]?.trim() || "",
              customerState: stateMatch?.[1]?.trim() || "",
              customerZip: zipMatch?.[1]?.trim() || "",
              productName: prod?.name || "",
              eventDate: res.event_date,
              totalPrice: res.total_price,
              reservationNumber: res.reservation_number,
            }).catch(console.error);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [reservationId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <PartyPopper className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h2>
        {reservation && (
          <p className="text-muted-foreground text-sm mb-1">Reservation #{reservation.reservation_number}</p>
        )}
        <p className="text-foreground mb-6">
          Your reservation is confirmed. A confirmation email with all the details will be sent to you shortly.
        </p>

        {reservation && (
          <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6 text-left space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Reservation fee of <strong>${reservation.reservation_fee_paid}</strong> paid</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Event date secured: <strong>{reservation.event_date}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span>Confirmation email on its way</span>
            </div>
          </div>
        )}

        {reservation && (
          <p className="text-xs text-muted-foreground mb-6">
            Remaining balance of <strong>${reservation.balance_due}</strong> is due before delivery. We'll contact you to arrange payment.
          </p>
        )}

        <Link to="/">
          <Button className="w-full">Back to Home</Button>
        </Link>
      </div>
    </div>
  );
}